import React, { useState } from 'react';
import { Link } from 'gatsby';
import Button from '@ingka/button';
import SSRIcon from '@ingka/ssr-icon';
import upIcon from '@ingka/ssr-icon/paths/chevron-up';
import styled from 'styled-components';
import { useTranslation } from '@ikea-customer-self-serve/gatsby-plugin-translations';
import variables from '@ingka/variables/design-tokens';
import cssIngka from '@ingka/variables/colours-css';
import { useCart } from '../contexts/Cart';
import { ItemImage } from './Item';
import { useToggleBoolean } from '../hooks/useToggleBoolean';
import { CartItemDisplay } from './CartItemDisplay';
import { AspectRatioImage } from './AspectRatioImage';
import TMarkUp from './TMarkUp';

const StyledButton = styled(Button)`
  font-size: 16px;
`;
const HorizontalItems = styled.div`
  display: flex;
  ${ItemImage}:nth-child(1) {
    margin-inline-start: 0px;
  }
`;
const MobileCartHeader = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 24px;
`;
const StyledOpenButtonContainer = styled.div`
  background: ${cssIngka.colourNeutral1};
  z-index: 2;
  padding: 0 24px;
  height: 95px;
`;
const RotateIcon = styled.span`
  position: absolute;
  right: 20px;
  display: flex;
  align-items: center;
  height: var(--height);
  &[data-rotate='true'] {
    svg {
      transform: rotate(180deg);
    }
  }
`;
const MobileCartLayout = styled.div`
  --height: 80px;
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 200;
  transform: translateY(100%);
  width: 100%;
  transition-duration: 400ms; // TODO: Take these from @ingka/variables/_design-tokens.scss
  transition-timing-function: cubic-bezier(0.4, 0, 0.4, 1);
  display: flex;
  flex-direction: column;
  &[data-open='true'] {
    transform: translateY(0);
  }
`;
const MobileCartItems = styled.div`
  width: 100%;
  cursor: pointer;
  height: var(--height);
  background: ${cssIngka.colourNeutral1};
  border-top: 1px solid ${cssIngka.colourNeutral3};
  transition-duration: 400ms; // TODO: Take these from @ingka/variables/_design-tokens.scss
  transition-timing-function: cubic-bezier(0.4, 0, 0.4, 1);
  &[data-open='true'] {
    height: calc(100vh - 200px);
  }
`;

const VerticalItems = styled.div`
  padding: 0 24px;
  height: calc(100% - 80px);
  overflow-y: scroll;

  --distance: 65px;
  --width: 8px;

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    width: var(--distance);
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: var(--distance);
    border: calc((var(--distance) / 2) - (var(--width) / 2)) solid
      rgba(0, 0, 0, 0);
    background-clip: padding-box;
    background-color: ${cssIngka.colourNeutral3};
  }
`;
const CircleButton = styled.span`
  display: block;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${cssIngka.colourNeutral3};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${variables.fontWeightBold};
  color: ${cssIngka.colourNeutral7};
`;

const MobileCart = (): JSX.Element => {
  const [open, toggleOpen] = useToggleBoolean(false);
  const [inView, setInView] = useState(false);
  const [extraItems, setExtraItems] = useState(0);
  const Cart = useCart();
  const { t } = useTranslation();
  React.useEffect(() => {
    setInView(Cart.state.items.size > 0);
    setExtraItems(Math.max(Cart.state.items.size - 3, 0));
  }, [Cart.state.items, setInView]);

  const horizontalItems = Array.from(Cart.state.items.values())
    .slice(0, 3)
    .map((item, index) => (
      <ItemImage key={index}>
        <AspectRatioImage src={item.item.imageSrc} alt="" />
      </ItemImage>
    ));

  if (extraItems > 0) {
    horizontalItems.push(<CircleButton>{`+${extraItems}`}</CircleButton>);
  }

  const verticalItems = Array.from(
    Cart.state.items.values()
  ).map(({ item, quantity }) => (
    <CartItemDisplay key={item.id} item={item} quantity={quantity} />
  ));

  return (
    <MobileCartLayout data-open={inView}>
      <MobileCartItems data-open={open}>
        <MobileCartHeader onClick={toggleOpen}>
          {open ? (
            <h3 className="h3">
              <TMarkUp value={t(`spareParts.orderList`)} />
            </h3>
          ) : (
            <HorizontalItems>{horizontalItems}</HorizontalItems>
          )}
          <RotateIcon data-rotate={open}>
            <SSRIcon paths={upIcon} />
          </RotateIcon>
        </MobileCartHeader>
        {open && <VerticalItems>{verticalItems}</VerticalItems>}
      </MobileCartItems>
      <StyledOpenButtonContainer>
        <Link to="/customer-information/">
          <StyledButton type="emphasised" fluid>
            <TMarkUp value={t('spareParts.orderSpareParts')} />
          </StyledButton>
        </Link>
      </StyledOpenButtonContainer>
    </MobileCartLayout>
  );
};

export default MobileCart;
